'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AlinBolgesineDolguUygulamasiNedirNasilYapilirPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/alin-bolgesine-dolgu-uygulamasi-nedir-nasil-yapilir';
  const pageTitle = 'Alın Bölgesine Dolgu Uygulaması Nedir, Nasıl Yapılır?';
  const pageDescription = 'Alın dolgusu kırışıklıkları azaltır, yüz hatlarını dengeleyip genç görünüm sağlar. Nasıl yapılır, hangi dolgu maddeleri kullanılır, etkisi ne kadar sürer?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Alın Bölgesine Dolgu Uygulaması', href: '/tr/blog/alin-bolgesine-dolgu-uygulamasi-nedir-nasil-yapilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Alın bölgesine dolgu uygulaması ve yüz yenileme tedavisi değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Alın Dolgusu
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Alın Bölgesine Dolgu Uygulaması Nedir, Nasıl Yapılır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>2 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Alın bölgesi, yüz ifadesinin en belirgin olduğu alanlardan biridir. Zamanla mimik hareketleri, cilt elastikiyet kaybı ve yaşlanma nedeniyle alın bölgesinde yatay çizgiler, çöküklükler ve kırışıklıklar oluşur. Bu durum yüzü yorgun ve gergin gösterebilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Alın dolgusu, bu çizgileri yumuşatarak ve hacim kaybını gidererek daha genç, dinamik ve dengeli bir yüz görünümü sağlar.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Alın Dolgusu Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alın dolgusu, cilt altına hyaluronik asit veya benzeri dolgu maddelerinin enjekte edilmesiyle yapılan bir işlemdir. Amaç; alın bölgesindeki kırışıklıkları azaltmak, hacim kaybını gidermek ve cilt dokusunu desteklemektir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Dolgu maddesi, cildin su tutma kapasitesini artırarak doğal bir gerginlik ve pürüzsüzlük sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Alın Dolgusu Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Uygulama öncesi bölge temizlenir ve lokal anestezik krem sürülür.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-purple-800 space-y-2">
                  <li>Uygulama öncesi bölge temizlenir ve lokal anestezik krem sürülür</li>
                  <li>İnce uçlu iğneler veya kanül yardımıyla dolgu, alın çizgilerinin altına dikkatlice enjekte edilir</li>
                  <li>İşlem süresi yaklaşık 15–20 dakika sürer</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="text-purple-800 font-medium">🕒 Sonuçlar hemen görülür, ancak dolgunun tam oturması 3–5 gün sürebilir</p>
                  <p className="text-purple-800 font-medium">💎 Etki süresi ortalama 12–18 ay arasında değişir</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Kullanılan Dolgu Türleri
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu Maddesi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık Süresi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Hyaluronik Asit (HA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">En sık kullanılan, doğal ve güvenli madde</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalsiyum Hidroksiapatit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha yoğun yapı, lifting etkisi güçlü</td>
                      <td className="px-6 py-4 text-sm text-gray-900">18–24 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Polilaktik Asit (PLA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kolajen üretimini uyarır</td>
                      <td className="px-6 py-4 text-sm text-gray-900">18 ay ve üzeri</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Uygulama türü, kişinin yaşına, cilt yapısına ve kırışıklık derinliğine göre belirlenir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Alın Dolgusu ve Botoks Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Botoks
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim kazandırır, cilt altını doldurur</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas kasılmalarını azaltır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kullanım Alanı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Çöküklük, hacim kaybı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Mimik kaynaklı kırışıklıklar</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sonuç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında dolgunluk</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 gün içinde pürüzsüzlük</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kombinasyon</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Birlikte kullanılabilir</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Maksimum etki sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                <p className="text-yellow-800 font-medium">
                  💡 Kısacası: Botoks kas hareketini sınırlar, dolgu cilt dokusunu destekler. Birlikte uygulandığında sonuç daha doğal ve uzun ömürlü olur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Alın Dolgusunun Faydaları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Yüz ifadesini yumuşatır</li>
                  <li>✅ Kırışıklıkları ve çizgileri azaltır</li>
                  <li>✅ Ciltte gerginlik ve parlaklık sağlar</li>
                  <li>✅ Cerrahi gerektirmez</li>
                  <li>✅ Hızlı ve güvenli bir işlemdir</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Alın bölgesinde derin yatay çizgiler bulunanlar</li>
                    <li>Cilt elastikiyetini kaybetmiş kişiler</li>
                    <li>Botoks sonrası hala belirgin çöküklüğü olanlar</li>
                    <li>Cerrahi müdahaleye gerek duymadan gençleşmek isteyenler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Gebe veya emziren kadınlara</li>
                    <li>Aktif cilt enfeksiyonu olanlara</li>
                    <li>Kan sulandırıcı ilaç kullananlara</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İlk 24 saat masaj, sıcak duş, sauna yapılmamalıdır</li>
                  <li>Yüz üstü yatmaktan kaçınılmalıdır</li>
                  <li>İlk 12 saat makyaj önerilmez</li>
                  <li>Bol su içmek dolgunun daha uzun süre kalmasını sağlar</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Alın bölgesine dolgu uygulaması ve yüz yenileme tedavisi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Alın dolgusu doğal görünür mü?</h3>
                  <p className="text-gray-700">Evet. Deneyimli hekim tarafından doğru miktarda uygulandığında son derece doğal bir sonuç elde edilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Ortalama 12–18 ay kalıcıdır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. İşlem sonrası morarma olur mu?</h3>
                  <p className="text-gray-700">Nadiren hafif morluk görülebilir; genellikle birkaç gün içinde geçer.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Alın botoksu ile birlikte yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet. Kombine edildiğinde hem kas gevşemesi hem hacim desteği sağlanır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Kalıcı dolgu önerilir mi?</h3>
                  <p className="text-gray-700">Hayır. Yüz mimiklerine uygun olmayan kalıcı dolgular önerilmez; doğal hareketi kısıtlayabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "Dermal Fillers and Forehead Rejuvenation"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Alın Dolgusu Uygulama Kılavuzu</li>
                <li>Harvard Health Publishing – "Non-surgical Forehead Rejuvenation Techniques"</li>
                <li>Mayo Clinic – "Hyaluronic Acid Fillers: Benefits and Safety"</li>
                <li>ISAPS 2024 Global Report – "Injectable Trends in Facial Aesthetics"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#alındolgusu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#yüzyenileme</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Alın Dolgusu İçin Randevu Alın</h3>
              <p className="mb-6">Yüz yenileme ve alın dolgusu tedavisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Alın dolgusu hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Cerrahi olmayan yüz gençleştirmenin bilimsel açıklaması.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-dolgu-farklari"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks ile Dolgu Arasındaki Farklar</h4>
              <p className="text-gray-600 text-sm">Hangi uygulama ne zaman tercih edilmeli?</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
